import {
  ParentSearchSuggestionsQuery as ParentSearchSuggestionsQueryType,
  ParentSearchSuggestionsQueryVariables,
} from "@outschool/gql-frontend-generated";
import { useLazyQuery } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  ParentSearchSuggestionsQuery,
  SearchItem,
  useSavedSearches,
} from "@outschool/ui-components-website";
import isNil from "lodash/isNil";
import omit from "lodash/omit";
import omitBy from "lodash/omitBy";
import { useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";

import { getSearchHistory } from "../../../client/components/search/RecentSearchHistory";

import type { SavedSearch } from "@outschool/gql-backend-generated";

export function useSearchSuggestions(size?: number, ignore: string[] = []) {
  const [fetchSuggestions, { data, loading, error }] = useLazyQuery<
    ParentSearchSuggestionsQueryType,
    ParentSearchSuggestionsQueryVariables
  >(ParentSearchSuggestionsQuery, { variables: { query: "", ignore, size } });

  return {
    loading,
    error,
    fetchSuggestions,
    popularTermSuggestions: data?.searchSuggestions?.popularTermSuggestions,
    topicSuggestions: data?.searchSuggestions?.topicSuggestions,
    teacherSuggestions: data?.searchSuggestions?.teacherSuggestions,
  };
}

/**
 * Computes search suggestions based on input and available data sources.
 * Returns memoized suggestions instead of setting state via effects.
 */
export function useSearchSuggestionsData({
  popularTermSuggestions,
  topicSuggestions,
  teacherSuggestions,
  placeholderSuggestions,
  inputValue,
}: {
  popularTermSuggestions?: ParentSearchSuggestionsQueryType["searchSuggestions"]["popularTermSuggestions"];
  topicSuggestions?: ParentSearchSuggestionsQueryType["searchSuggestions"]["topicSuggestions"];
  teacherSuggestions?: ParentSearchSuggestionsQueryType["searchSuggestions"]["teacherSuggestions"];
  placeholderSuggestions: SearchItem[];
  inputValue: string;
}): SearchItem[] {
  const { isLoggedIn } = useSession();
  const { savedSearches, refetch: refetchSavedSearches } = useSavedSearches();
  const [recentSearches, setRecentSearches] = useState<SavedSearch[]>([]);

  // Fetch saved searches when input is cleared (for logged-in users)
  useEffect(() => {
    if (isLoggedIn && inputValue === "") {
      refetchSavedSearches();
      setRecentSearches(getSearchHistory());
    }
  }, [inputValue, refetchSavedSearches, isLoggedIn]);

  // Compute suggestions as a memo based on input and available data
  const suggestions = useMemo((): SearchItem[] => {
    if (inputValue) {
      const result: SearchItem[] = [];

      topicSuggestions?.forEach(t => {
        result.push({
          uid: t.uid,
          name: t.label,
          category: t.category ?? undefined,
          type: "topic",
        });
      });

      popularTermSuggestions
        ?.filter(t => !result.some(el => el.name === t.subCategory))
        .forEach(t => {
          result.push({
            uid: t.uid,
            category: t.category,
            name: t.subCategory,
            type: "popular",
          });
        });

      teacherSuggestions?.forEach(t => {
        result.push({
          uid: t.uid,
          name: t.name || "",
          type: "teacher",
          photo: t.photo,
          leaderLink: t.leader_link,
        });
      });

      result.push({
        uid: "keyword",
        name: inputValue,
        type: "keyword",
      });

      return result;
    }

    return addRecentAndSavedSearches(
      recentSearches,
      savedSearches,
      placeholderSuggestions,
    );
  }, [
    inputValue,
    topicSuggestions,
    popularTermSuggestions,
    teacherSuggestions,
    recentSearches,
    savedSearches,
    placeholderSuggestions,
  ]);

  return suggestions;
}

export function addRecentAndSavedSearches(
  recentSearches: SavedSearch[],
  savedSearches: SavedSearch[],
  placeholderSuggestions: SearchItem[],
): SearchItem[] {
  type SearchItemWithCreatedAt = SearchItem & { createdAt: Date };

  let unsortedSuggestions: SearchItemWithCreatedAt[] = [];

  if (savedSearches) {
    savedSearches.forEach((s: SavedSearch) => {
      const newEntry: SearchItemWithCreatedAt = {
        uid: s.filters.userUid ?? v4(),
        name: s.filters.q || s.filters.userName || "",
        type: "saved",
        createdAt: new Date(s.createdAt),
        searchFilters: omitBy(omit(s.filters, ["__typename", "q"]), isNil),
      };

      if (isEmptySearchItem(newEntry, ["order"])) {
        return;
      }

      // there shouldn't be duplicates among saved searches
      // and we don't remove similar searches
      unsortedSuggestions.push(newEntry);
    });
  }

  if (recentSearches) {
    recentSearches.forEach((s: SavedSearch) => {
      const newEntry: SearchItemWithCreatedAt = {
        uid: s.filters.userUid ?? v4(),
        name: s.filters.q || s.filters.userName || "",
        type: "recent",
        createdAt: new Date(s.createdAt),
        searchFilters: omitBy(omit(s.filters, ["__typename", "q"]), isNil),
      };

      if (isEmptySearchItem(newEntry, ["order"])) {
        return;
      }

      const similarSearchIndex = unsortedSuggestions.findIndex(
        el => el.name === newEntry.name,
      );
      const similarSearch = unsortedSuggestions[similarSearchIndex];

      if (similarSearch) {
        if (
          similarSearch.type !== newEntry.type &&
          newEntry.createdAt.getTime() > similarSearch.createdAt.getTime()
        ) {
          // if types are different, this means one must be
          // a recent search and the other a saved search
          // in this case, we want to keep the saved search
          // but with the datetime of the newer search
          unsortedSuggestions.splice(similarSearchIndex, 1);
          newEntry.type = similarSearch.type;
          newEntry.searchFilters = similarSearch.searchFilters;
          unsortedSuggestions.push(newEntry);
        } else if (
          newEntry.createdAt.getTime() > similarSearch.createdAt.getTime()
        ) {
          // if types are the same, we want to keep the newer search
          unsortedSuggestions.splice(similarSearchIndex, 1);
          unsortedSuggestions.push(newEntry);
        }
      } else {
        unsortedSuggestions.push(newEntry);
      }
    });
  }

  const sortedSuggestions: SearchItem[] = unsortedSuggestions.sort((a, b) => {
    return b.createdAt.getTime() - a.createdAt.getTime();
  });

  if (placeholderSuggestions) {
    placeholderSuggestions.forEach((s: SearchItem) => {
      // if there is an existing similar search, we don't add it

      const similarSearch = unsortedSuggestions.find(
        el => el.name?.toLowerCase() === s.name?.toLowerCase(),
      );

      if (similarSearch) {
        return;
      }

      sortedSuggestions.push(s);
    });
  }

  return sortedSuggestions.slice(0, 10);
}

export function isEmptySearchItem(
  searchItem: SearchItem,
  filtersToIgnore: string[] = [],
): boolean {
  if (searchItem.name !== "") {
    return false;
  }

  const searchItemWithoutFilters = omitBy(
    omit(searchItem.searchFilters, filtersToIgnore),
    isNil,
  );

  if (searchItemWithoutFilters.q === "") {
    delete searchItemWithoutFilters.q;
  }

  return (
    !searchItemWithoutFilters ||
    Object.keys(searchItemWithoutFilters).length === 0
  );
}
